import { useState } from 'react';
import axios from 'axios';
import { ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { StatusDot } from '@/components/Page';
import { API_URL } from '@/contexts/AuthContext';
import { formatBRL, formatDataHora } from '@/lib/format';
import { comRespostaBelluno, descricaoPagamentoBelluno, linkBelluno, textoVencimento, tomDoLink } from '@/lib/belluno';

// Decisão da análise da Belluno (belluno_analyses / client_analyses.decision).
const ANALISE = { approved: 'aprovada', declined: 'recusada', refuse: 'recusada', refused: 'recusada' };

// O link da Belluno como o financeiro vê na Gestão e no Financeiro: estado, como foi pago, estornos e o
// botão de reconsultar (a Belluno avisa por postback; o botão cobre o aviso que não chegou).
const Campo = ({ rotulo, children }) => (
  <>
    <dt className="truncate py-[3px] text-gray-500">{rotulo}</dt>
    <dd className="min-w-0 py-[3px] font-medium [overflow-wrap:anywhere]">{children}</dd>
  </>
);

// `parte`: o link de uma parte Belluno do pagamento dividido em vez do link do pedido inteiro.
export default function PagamentoBelluno({ pedido, parte = null, onChanged }) {
  const link = linkBelluno(pedido, parte);
  const [atualizando, setAtualizando] = useState(false);
  if (!link?.transaction_id) return null;
  const valor = link.valor || (parte == null ? pedido.total : (pedido.pagamento_split || []).find((p) => p.indice === parte)?.valor);

  const atualizar = async () => {
    setAtualizando(true);
    try {
      // A reconsulta atualiza todos os links do pedido; o aviso fala deste.
      const { data } = await axios.post(`${API_URL}/pedidos/${pedido.id}/belluno/sincronizar`, null);
      const novo = linkBelluno(comRespostaBelluno(pedido, data), parte);
      toast.success(novo?.pago ? 'Pagamento confirmado pela Belluno' : `Link ${String(novo?.status_label || '').toLowerCase()}`);
      onChanged?.(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Não foi possível consultar a Belluno');
    } finally {
      setAtualizando(false);
    }
  };

  const como = descricaoPagamentoBelluno(link);
  const vencimento = link.aberto ? textoVencimento(link) : '';
  const estornos = (link.estornos || []).filter((e) => e && (e.valor || e.amount));

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-sheet2">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 p-4">
        <div className="min-w-0">
          <StatusDot tone={tomDoLink(link)} className="text-[12.5px]">
            {link.status_label}{vencimento ? `, ${vencimento}` : ''}
          </StatusDot>
          <p className="mt-1.5 text-[15px] font-medium leading-snug">
            {link.pago ? `Pago por ${como}` : 'Link de pagamento Belluno'}
          </p>
          <p className="mt-0.5 text-[12.5px] text-gray-500">
            {[link.titular, link.pago_em && `pago em ${formatDataHora(link.pago_em)}`,
              !link.pago && link.gerado_em && `gerado em ${formatDataHora(link.gerado_em)}`,
              link.gerado_por && `por ${link.gerado_por}`].filter(Boolean).join(' · ')}
          </p>
          {link.motivo && !link.pago && <p className="mt-1.5 text-[13px] text-red-700 dark:text-red-300">{link.motivo}</p>}
        </div>
        <div className="text-right">
          <p className="text-[22px] font-medium leading-none tracking-[-0.02em] tabular-nums">{formatBRL(valor)}</p>
          <p className="mt-1.5 text-[12px] text-gray-500">
            {[link.autorizacao && `aut. ${link.autorizacao}`, link.antifraude && `análise ${ANALISE[String(link.antifraude).toLowerCase()] || String(link.antifraude).toLowerCase()}`,
              link.estornado > 0 && `estornado ${formatBRL(link.estornado)}`].filter(Boolean).join(' · ')}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-2">
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 text-[12.5px]">
          <Campo rotulo="Transação">{link.transaction_id}</Campo>
          {link.cartao && <Campo rotulo="Cartão">{link.cartao}</Campo>}
          {link.nsu && <Campo rotulo="NSU">{link.nsu}</Campo>}
          {estornos.map((e, i) => (
            <Campo key={i} rotulo="Estorno">
              {formatBRL(e.valor ?? e.amount)}{e.status ? `, ${String(e.status).toLowerCase()}` : ''}{e.em ? ` em ${formatDataHora(e.em)}` : ''}
            </Campo>
          ))}
        </dl>
        <div className="flex items-center gap-1">
          {link.link && (
            <Button asChild size="sm" variant="ghost" className="h-8 text-blue-600 dark:text-blue-400">
              <a href={link.link} target="_blank" rel="noopener noreferrer">Abrir link <ExternalLink className="ml-1 h-3 w-3" /></a>
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-8" onClick={atualizar} disabled={atualizando} data-testid="belluno-atualizar">
            {atualizando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Atualizar
          </Button>
        </div>
      </div>
    </div>
  );
}
